import { NavigateFunction } from 'react-router-dom';
import { MallUser } from './MallAuthModal';

interface Props {
  lang: 'zh' | 'en';
  setLang: (l: 'zh' | 'en') => void;
  cartCount: number;
  navigate: NavigateFunction;
  user?: MallUser | null;
  onLoginClick?: () => void;
}

const LEVEL_COLORS: Record<string, string> = { EXPLORER: '#00FFCC', PIONEER: '#FFD700', NEXUS_VIP: '#FF6B9D', GALAXY_ELITE: '#A855F7' };

export default function MallNavbar({ lang, setLang, cartCount, navigate, user, onLoginClick }: Props) {
  return (
    <nav className="fixed top-0 left-0 w-full z-50" style={{ background: 'rgba(1,2,8,0.94)', borderBottom: '1px solid rgba(255,200,50,0.12)', backdropFilter: 'blur(24px)' }}>
      <div className="max-w-[1800px] mx-auto px-6 h-16 flex items-center justify-between">
        {/* Left: Back + Logo */}
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-sm cursor-pointer whitespace-nowrap transition-all hover:scale-105" style={{ color: 'rgba(255,200,50,0.55)' }}>
            <i className="ri-arrow-left-line" /><span className="hidden sm:inline">{lang === 'zh' ? '返回META66' : 'Back to META66'}</span>
          </button>
          <div className="w-px h-6" style={{ background: 'rgba(255,255,255,0.08)' }} />
          <div className="flex items-center gap-3">
            <div className="relative w-10 h-10 flex items-center justify-center flex-shrink-0">
              <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(255,200,50,0.3), rgba(255,100,50,0.3))', border: '2px solid rgba(255,200,50,0.6)', borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%', boxShadow: '0 0 20px rgba(255,200,50,0.4)' }} />
              <i className="ri-store-3-line relative z-10 text-lg" style={{ color: '#FFD700' }} />
            </div>
            <div>
              <div className="font-black text-sm tracking-widest" style={{ fontFamily: 'Orbitron, sans-serif', background: 'linear-gradient(90deg, #FFD700, #FF6B35, #FF4081)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>NEXUS OMNI MALL</div>
              <div className="text-xs" style={{ color: 'rgba(255,200,50,0.4)', letterSpacing: '0.1em' }}>{lang === 'zh' ? '星际元宇宙商城 · 购未来，在星际' : 'Interstellar Metaverse Commerce'}</div>
            </div>
          </div>
        </div>

        {/* Center Nav */}
        <div className="hidden xl:flex items-center gap-0.5">
          {[
            { label: { zh: '商城介绍', en: 'About' }, href: '#intro', icon: 'ri-information-line' },
            { label: { zh: '20大分区', en: 'Zones' }, href: '#zones', icon: 'ri-grid-line' },
            { label: { zh: '购物指引', en: 'Guide' }, href: '#guide', icon: 'ri-compass-3-line' },
            { label: { zh: '精选商品', en: 'Featured' }, href: '#featured', icon: 'ri-star-line' },
            { label: { zh: '星际物流', en: 'Logistics' }, href: '#logistics', icon: 'ri-rocket-line' },
            { label: { zh: 'Google Earth', en: 'Earth View' }, href: '#earth', icon: 'ri-earth-line' },
            { label: { zh: '月球/火星', en: 'Moon/Mars' }, href: '#space', icon: 'ri-moon-line' },
          ].map(item => (
            <a key={item.href} href={item.href} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap hover:bg-white/5" style={{ color: 'rgba(255,255,255,0.45)' }}>
              <i className={`${item.icon} text-xs`} style={{ color: '#FFD700' }} />{item.label[lang]}
            </a>
          ))}
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2">
          <button onClick={() => setLang(lang === 'zh' ? 'en' : 'zh')} className="px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer whitespace-nowrap transition-all hover:scale-105" style={{ background: 'rgba(255,215,0,0.1)', border: '1px solid rgba(255,215,0,0.3)', color: '#FFD700' }}>{lang === 'zh' ? 'EN' : '中'}</button>
          <button onClick={() => navigate('/omniverse-mall/products')} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold cursor-pointer whitespace-nowrap transition-all hover:scale-105" style={{ background: 'rgba(255,215,0,0.08)', border: '1px solid rgba(255,215,0,0.25)', color: '#FFD700' }}>
            <i className="ri-search-line" /><span className="hidden sm:inline">{lang === 'zh' ? '全部商品' : 'All Products'}</span>
          </button>
          {user ? (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-pointer" style={{ background: `${LEVEL_COLORS[user.level]}10`, border: `1px solid ${LEVEL_COLORS[user.level]}30` }}>
              <img src={user.avatar} alt={user.name} className="w-6 h-6 rounded-lg object-cover object-top flex-shrink-0" />
              <div className="hidden sm:block">
                <div className="text-xs font-bold truncate max-w-[80px]" style={{ color: LEVEL_COLORS[user.level] }}>{user.name.split('·')[0].trim()}</div>
                <div className="text-xs" style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.55rem', fontFamily: 'Orbitron, sans-serif' }}>{user.level}</div>
              </div>
            </div>
          ) : (
            <button onClick={onLoginClick} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold cursor-pointer whitespace-nowrap transition-all hover:scale-105" style={{ background: 'rgba(0,255,204,0.08)', border: '1px solid rgba(0,255,204,0.25)', color: '#00FFCC' }}>
              <i className="ri-user-line" /><span className="hidden sm:inline">{lang === 'zh' ? '登录' : 'Login'}</span>
            </button>
          )}
          <button className="relative flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold cursor-pointer whitespace-nowrap transition-all hover:scale-105" style={{ background: 'linear-gradient(135deg, rgba(255,215,0,0.18), rgba(255,107,53,0.18))', border: '1px solid rgba(255,215,0,0.4)', color: '#FFD700' }}>
            <i className="ri-shopping-cart-2-line" /><span className="hidden sm:inline">{lang === 'zh' ? '购物车' : 'Cart'}</span>
            {cartCount > 0 && <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full text-xs font-black flex items-center justify-center text-black" style={{ background: '#FF4081' }}>{cartCount}</span>}
          </button>
        </div>
      </div>
    </nav>
  );
}
